/**
 * Image-provider safety blocks (ENG-1155).
 *
 * Both image providers can refuse a generation on safety grounds, and each
 * says so differently:
 *
 *   - OpenAI images API: HTTP 400 with `error.code: "moderation_blocked"`
 *     (gpt-image) or `"content_policy_violation"` (older models). The body may
 *     carry `moderation_details.moderation_stage` (`input` | `output`) and a
 *     category list, either as `moderation_details.categories` or inside the
 *     message as `safety_violations=[sexual]`.
 *   - Gemini: HTTP 200 with `promptFeedback.blockReason` (the prompt was
 *     refused) or a candidate `finishReason` such as `IMAGE_SAFETY` /
 *     `PROHIBITED_CONTENT` (the output was refused) and no image part.
 *
 * Before this module the OpenAI block collapsed into a generic
 * `INTERNAL_ERROR` (input stage) or a retryable `EXTERNAL_SERVICE_ERROR`
 * (output stage), and a Gemini `finishReason` block looked like an empty
 * response and was retried on two models. Every caller now detects the block
 * here, surfaces it as `ErrorCodes.AI_SAFETY_BLOCKED`, and treats it as
 * terminal.
 *
 * The detected block is for support triage only. `provider`, `category` and
 * `stage` go to structured logs and analytics, never to the owner. Owners see
 * `IMAGE_SAFETY_BLOCK_USER_MESSAGE`.
 *
 * Kept free of runtime dependencies beyond the shared error codes, so the
 * detectors stay pure and cheap to unit-test.
 */
import { ErrorCodes } from './errors.js';
/**
 * The concise, owner-safe explanation. Deliberately says nothing about the
 * provider or the category: it names what happened and what may help.
 */
export const IMAGE_SAFETY_BLOCK_USER_MESSAGE = "The image service's safety checks blocked this image. Different wording or imagery may help.";
const OPENAI_SAFETY_CODES = new Set([
    'moderation_blocked',
    'content_policy_violation',
]);
/**
 * Gemini `finishReason` values that mean "refused on safety grounds".
 * `RECITATION` / `IMAGE_RECITATION` (copyright) and `OTHER` / `NO_IMAGE` are
 * deliberately excluded: they are not safety verdicts, so they keep the
 * existing soft no-image retry.
 */
export const GEMINI_SAFETY_FINISH_REASONS = new Set([
    'SAFETY',
    'BLOCKLIST',
    'PROHIBITED_CONTENT',
    'SPII',
    'IMAGE_SAFETY',
    'IMAGE_PROHIBITED_CONTENT',
]);
const asStage = (value) => value === 'input' || value === 'output' ? value : 'unknown';
/**
 * Detect an OpenAI images-API safety block from a failed response.
 * Returns null for every other failure, including a bare 400.
 */
export function detectOpenAiImageSafetyBlock(status, body) {
    if (status < 400 || status >= 500)
        return null;
    let parsed = null;
    try {
        parsed = JSON.parse(body);
    }
    catch {
        parsed = null;
    }
    const code = typeof parsed?.error?.code === 'string' ? parsed.error.code : undefined;
    const isBlock = code
        ? OPENAI_SAFETY_CODES.has(code)
        : /moderation_blocked|content_policy_violation/.test(body);
    if (!isBlock)
        return null;
    const details = parsed?.error?.moderation_details;
    const stage = details?.moderation_stage !== undefined
        ? asStage(details.moderation_stage)
        : asStage(/"moderation_stage"\s*:\s*"(input|output)"/.exec(body)?.[1]);
    const categories = Array.isArray(details?.categories)
        ? details.categories.filter((c) => typeof c === 'string')
        : [];
    const message = typeof parsed?.error?.message === 'string' ? parsed.error.message : body;
    const fromMessage = /safety_violations=\[([^\]]*)\]/
        .exec(message)?.[1]
        ?.split(',')
        .map((c) => c.trim())
        .filter(Boolean);
    const category = categories.join(',') ||
        fromMessage?.join(',') ||
        code ||
        'moderation_blocked';
    return { provider: 'openai', category, stage };
}
/**
 * Detect a Gemini safety block from a 200 response body. A `blockReason` is
 * a request-level block. A safety `finishReason` is an output-level block.
 */
export function detectGeminiImageSafetyBlock(json) {
    const blockReason = json.promptFeedback?.blockReason;
    if (blockReason) {
        return { provider: 'gemini', category: blockReason, stage: 'input' };
    }
    const finishReason = json.candidates?.find((c) => c.finishReason && GEMINI_SAFETY_FINISH_REASONS.has(c.finishReason))?.finishReason;
    if (finishReason) {
        return { provider: 'gemini', category: finishReason, stage: 'output' };
    }
    return null;
}
const isSafetyBlock = (value) => {
    const v = value;
    return (!!v &&
        typeof v === 'object' &&
        (v.provider === 'openai' || v.provider === 'gemini') &&
        typeof v.category === 'string');
};
/**
 * Build the `details` a FeatureError(AI_SAFETY_BLOCKED) carries, so any layer
 * can recover the block with `getImageSafetyBlock`.
 */
export const imageSafetyBlockDetails = (block) => ({
    // Same `outcome` tag the Gemini transport has always used for a block, so
    // existing log filters keep matching.
    outcome: 'blocked',
    provider: block.provider,
    providerCategory: block.category,
    stage: block.stage,
});
/**
 * Recover the safety block from anything a pipeline might throw or return:
 * an `OpenAiImageError`, a `FeatureError(AI_SAFETY_BLOCKED)`, or a wrapper
 * holding either on `cause` / `originalError`. Matched structurally, not with
 * `instanceof`, so wrappers from other packages (the worker's
 * `GraphicJobError`) are seen too. Returns null when the failure is not a
 * safety block.
 */
export function getImageSafetyBlock(error) {
    let current = error;
    for (let depth = 0; depth < 5 && current; depth++) {
        const e = current;
        if (typeof e !== 'object')
            return null;
        if (isSafetyBlock(e.safetyBlock))
            return e.safetyBlock;
        const flagged = e.code === ErrorCodes.AI_SAFETY_BLOCKED ||
            e.featureCode === ErrorCodes.AI_SAFETY_BLOCKED;
        if (flagged) {
            // A wrapper (the worker's GraphicJobError) may only carry the code;
            // prefer the richer block its cause holds.
            const inner = e.details
                ? null
                : getImageSafetyBlock(e.originalError ?? e.cause);
            if (inner)
                return inner;
            const d = e.details ?? {};
            return {
                provider: d.provider === 'gemini' ? 'gemini' : 'openai',
                category: typeof d.providerCategory === 'string'
                    ? d.providerCategory
                    : 'unknown',
                stage: asStage(d.stage),
            };
        }
        current = e.originalError ?? e.cause;
    }
    return null;
}
//# sourceMappingURL=image-safety-block.js.map